// @flow
import type { StoreState } from 'reducers';
import type { RuleType } from 'types/rule-types';
import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { getRuleTypes } from 'actions/rule-types';
import Rule from './Rule';
import { getPathFromEvent } from 'utils/compatibility-utils';
import Toastr from 'utils/toastr-util';

import {
  addUidToRule,
  removeUidFromRule,
  findRule,
  getRuleType,
  getDefaultOperator,
} from './utils';

type Props = {
  setRef: Function,
  ruleTypes: Array<RuleType>,
  ruleTypesFilter?: Function,
  type: string,
  getRuleTypes: () => void,
  defaultValue: Object,
  onChange: Function,
  readonly: boolean,
  config: Object,
  extraRuleTypes: Array<RuleType>,
  ruleBuilderSearchParams: Object,
  ruleTypesPassedFromSystem?: boolean,
  systemType?: string
};

const DEFAULT_TIME_RANGE = [0, 86399];
const DEFAULT_RULE = {
  operator: 'and',
  conditions: [],
};

const internalConditions = ['var.action.randomizedValue', 'var.experiment.group'];

export class RuleBuilder extends Component {
  props: Props;

  constructor(props: Props) {
    super(props);

    let rule = props.defaultValue;

    if (_.isNil(rule) || _.isEmpty(rule)) {
      rule = DEFAULT_RULE;
    }

    this.state = {
      currentlySelected: null,
      currentlyHovered: null,
      rule: this.preprocessRule(rule),
      ruleTypes: [],
      currentOperatorRuleUid: null,
    };
    if (this.props.setRef) {
      this.props.setRef(this);
    }
  }

  componentDidMount() {
    document.addEventListener('click', this.handleRuleDeselection);
    if (
      !this.props.ruleTypesPassedFromSystem &&
      _.isEmpty(this.props.ruleTypes) &&
      this.props.type !== ''
    ) {
      this.props.getRuleTypes(
        this.props.type,
        this.props.ruleBuilderSearchParams
      );
    }
  }
  UNSAFE_componentWillUpdate(nextProps: Props) {
    if (
      !this.props.ruleTypesPassedFromSystem &&
      nextProps.type !== '' &&
      this.shouldFetchRuleTypes(this.props, nextProps)
    ) {
      this.props.getRuleTypes(
        nextProps.type,
        nextProps.ruleBuilderSearchParams
      );
    }
  }

  updateLocalState(rule: any) {
    if (_.isNil(rule) || _.isEmpty(rule)) {
      rule = DEFAULT_RULE;
    }
    this.setState({
      rule: this.preprocessRule(rule),
    });
  }

  shouldFetchRuleTypes(currentProps: Props, nextProps: Props) {
    if (
      !_.isEqual(
        currentProps.ruleBuilderSearchParams,
        nextProps.ruleBuilderSearchParams
      )
    ) {
      return true;
    }

    const keys = _.keys(currentProps.ruleBuilderSearchParams);
    for (let i = 0; i < keys.length; i += 1) {
      if (currentProps[keys[i]] !== nextProps[keys[i]]) {
        return true;
      }
    }
    return false;
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleRuleDeselection);
  }

  handleRuleHover = (ruleUid: number) => {
    if (this.props.readonly) return;
    this.setState({ currentlyHovered: ruleUid });
  };

  handleRuleUnHover = () => {
    if (this.props.readonly) return;
    this.setState({ currentlyHovered: null });
  };

  handleRuleSelection = (ruleUid: number) => {
    if (this.props.readonly) return;
    this.setState({ currentlySelected: ruleUid });
  };

  handleRuleDeselection = (e: Object) => {
    const path = getPathFromEvent(e);
    if (
      _.find(path, el => _.get(el, 'className') === 'rules-container') !==
      undefined
    ) {
      return;
    }
    this.setState({ currentlySelected: null });
  };

  handleRuleChange = (updatedRule: Rule, error: string) => {
    if (error) {
      return Toastr.error(`Error in rule builder: ${error}`);
    }

    const { rule } = this.state;
    const { rule: currRule } = findRule(rule, updatedRule.uid);
    _.assign(currRule, updatedRule);
    this.setState({ rule }, () => {
      if (_.isFunction(this.props.onChange)) {
        this.props.onChange(this.postprocessRule(this.state.rule));
      }
    });
  };

  handleOperatorSelection = (ruleUid: number, lhs: any) => {
    const ruleType = getRuleType(this.props.ruleTypes, lhs);

    const newRule = this.getNewRuleFromType(ruleType, lhs);

    this.insertRule(ruleUid, newRule);
  };

  getNewRuleFromType = (ruleType: Object, lhs: any) => {
    const defaultOperator = getDefaultOperator(ruleType);
    const ruleDisplayType = ruleType.displayType || ruleType.objectType;

    let newRule = null;
    switch (ruleDisplayType) {
      case 'bool':
        return { operator: defaultOperator, lhs, rhs: true };
      case 'object':
        return { operator: defaultOperator, lhs, rhs: {} };
      case 'dayOfWeek':
        return { operator: defaultOperator, lhs, rhs: [] };
      case 'timeOfDay':
        return { operator: defaultOperator, lhs, rhs: DEFAULT_TIME_RANGE };
      default:
        newRule = { operator: defaultOperator, lhs, rhs: '' };
    }

    if (_.isFunction(_.get(this.props.config.customInsert, lhs))) {
      newRule = this.props.config.customInsert[lhs](this.props.ruleTypes);
    }

    return newRule;
  };

  handleRuleRemove = (ruleUid: number) => {
    const baseRule = _.cloneDeep(this.state.rule);

    const removeRule = (uid: number) => {
      const { path: rulePath } = findRule(baseRule, uid);

      if (rulePath.length < 2) return;

      const { rule: parentRule } = findRule(
        baseRule,
        rulePath[rulePath.length - 2]
      );

      parentRule.conditions = parentRule.conditions.filter(
        cond => cond.uid !== uid
      );
      if (_.isEmpty(parentRule.conditions)) {
        removeRule(parentRule.uid);
      }
    };

    removeRule(ruleUid);

    this.setState({ rule: baseRule }, () =>
      this.props.onChange(this.postprocessRule(this.state.rule))
    );
  };

  insertRule = (rootUid: number, newRule: Object) => {
    const { rule } = this.state;
    const { rule: currRule } = findRule(rule, rootUid);

    currRule.conditions.push(newRule);
    this.setState({ rule: this.preprocessRule(rule) }, () =>
      this.props.onChange(this.postprocessRule(this.state.rule))
    );
  };

  preprocessRule = (aRule: Object) => {
    let rule = _.cloneDeep(aRule);

    // Prevent rules with errors to be pushed to state
    if (rule.errors) {
      rule = _.cloneDeep(DEFAULT_RULE);
    }

    if (rule.operator === 'or') {
      rule.conditions = [_.cloneDeep(rule)];
      rule.operator = 'and';
    }

    // create a new rule group only for top level rules
    if (_.isNil(rule.conditions)) {
      rule = {
        operator: 'and',
        conditions: [rule],
      };
    }

    if (_.isArray(rule.conditions)) {
      rule.conditions = rule.conditions.map(cond => {
        if (cond.operator !== 'or') {
          return {
            operator: 'or',
            conditions: [cond],
          };
        }
        return cond;
      });
    }

    addUidToRule(rule);
    return rule;
  };

  postprocessRule = (aRule: Object) => {
    if (!_.isNil(aRule.conditions) && _.isEmpty(aRule.conditions)) return null;

    let rule = _.cloneDeep(aRule);

    // remove rule group from top level rules that are singular
    while (rule.conditions && rule.conditions.length === 1) {
      rule = rule.conditions[0];
    }

    if (_.isArray(rule.conditions)) {
      rule.conditions = rule.conditions.map(cond => {
        if (cond.operator === 'or' && cond.conditions.length === 1) {
          return cond.conditions[0];
        }
        return cond;
      });
    }

    removeUidFromRule(rule);

    return rule;
  };

  render() {
    return (
      <div>
        <div className="rules-container">
          <Rule
            root
            rule={this.state.rule}
            ruleTypes={this.props.ruleTypes}
            currentlySelected={this.state.currentlySelected}
            currentlyHovered={this.state.currentlyHovered}
            onRuleHover={this.handleRuleHover}
            onRuleUnHover={this.handleRuleUnHover}
            onRuleSelection={this.handleRuleSelection}
            onRuleChange={this.handleRuleChange}
            onRuleRemove={this.handleRuleRemove}
            onOperatorSelection={this.handleOperatorSelection}
            readonly={this.props.readonly}
            config={this.props.config}
            systemType={this.props.systemType}
            cityIDs={this.props.cityIDs}
            rules={this.props.defaultValue}
          />
        </div>
      </div>
    );
  }
}

export function mapStateToProps(state: StoreState, ownProps: Object) {
  let ruleTypes;
  if (ownProps.ruleTypesPassedFromSystem) {
    ruleTypes = ownProps.ruleTypes;
  } else {
    ruleTypes = state.ruleTypes.entities;
    let type = ownProps.type;
    const typeSegs = _.split(ownProps.type, '.');
    if (typeSegs.length > 1) {
      type = typeSegs[typeSegs.length - 1];
    }
    ruleTypes = _.get(ruleTypes, type);
    if (
      ruleTypes &&
      ownProps.extraRuleTypes &&
      ownProps.extraRuleTypes.length > 0
    ) {
      ruleTypes = _.concat(ruleTypes, ownProps.extraRuleTypes);
    }
    if (ownProps.ruleTypesFilter) {
      ruleTypes = _.filter(ruleTypes, ownProps.ruleTypesFilter);
    }
    ruleTypes = _.filter(ruleTypes, ruleType => {
      return ruleType.group !== 'Scenario Limits';
    });
  }

  // hiding the internal conditions
  ruleTypes = _.filter(ruleTypes, ruleType => {
    return !internalConditions.includes(ruleType.variableName);
  });

  return {
    ruleTypes,
    getRuleTypesRequest: state.requests.getRuleTypesRequest || {},
  };
}

export default connect(mapStateToProps, {
  getRuleTypes,
})(RuleBuilder);

RuleBuilder.defaultProps = {
  ruleTypesPassedFromSystem: false,
  systemType: '',
};
